import React, {Component, ReactNode} from 'react'
import {createResidentPage, ResidentPagePropsSet} from '../shared/hoc/createResidentPage'
import ReservationsRepository from './ReservationsRepository'
import EventEmitterExResident from '../shared/pageBaseComponents/EventEmitterExResident'
import FacilityForReservation from '../facilities/FacilityForReservation'
import {
    FacilityReservation,
    NewDurationRentalFacilityReservation,
    NewOvernightRentalFacilityReservation,
} from './ReserveFacilityModel'
import {
    CenterPlainLinkStyledButton,
    PlainLinkStyledBackButton,
    PrimaryStyledButton,
} from '../../styledComponents/Button'
import {DisplayItemDividerForPageTop} from '../../styledComponents/DisplayItem'
import {NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'
import {HttpError} from '../../network/errors/HttpError'
import {ErrorResponseBody} from '../../network/response/ErrorResponseBody'
import {RequestError} from '../../network/response/RequestError'
import {Messages} from '../../Messages'
import {StyledPageTitle} from './components/CommonStyle'
import ResidentReserveFacilityConfirmComponent, {
    ReserveFacilityConfirmData,
} from './ResidentReserveFacilityConfirmComponent'
import {BlockSpacer} from '../../styledComponents/ResponsiveConvenience'
import {
    setResidentMyReservationsPageSearchDate,
} from './ResidentMyReservationsComponents/RMRReservationStatusesComponent'
import {
    clearResidentReserveTopPageSearchConditions,
} from './ResidentReserveFacilityTopComponents/RRFTFacilitySearchComponent'
import NatCurrentDateTimeRepo
    from '../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'
import {Nullable, Optional} from '../../app/TypeHelpers'
import moment, {Moment} from 'moment'
import {convertToMomentOrError, isCancelLimitOver} from './helpers/helpers'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faExclamationCircle} from '@fortawesome/free-solid-svg-icons'
import {Box} from '@mui/material'
import {
    NoticeMessageBody,
    NoticeMessageHeader,
    ReservationTypeTagForFirstCome,
    ReservationTypeTagForLottery,
    WarningMessageArea,
} from './ResidentReserveFacilityInputConfirmPage'
import {RESERVATION_TYPE} from '../../CodeMaster'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    facilityId: number
    reserveId: number
    reservationsRepo: ReservationsRepository
    currentDateTimeRepo: NatCurrentDateTimeRepo
    eventEmitter: EventEmitterExResident
    location: Location<LocationState>
}

interface LocationState {
    previousPageUrl: string
    stayAvailable: boolean
    facilityInfo: FacilityForReservation
    reserveInfo: FacilityReservation
}

interface State {
    currentDateTime: Nullable<Moment>
}

class ResidentReserveFacilityUpdateConfirmPage extends Component<Props & ResidentPagePropsSet, State> {
    constructor(props: Readonly<Props & ResidentPagePropsSet>) {
        super(props)

        this.state = {
            currentDateTime: null,
        }

        this.handleUpdateButtonClick = this.handleUpdateButtonClick.bind(this)
        this.handleBackLinkOnClick = this.handleBackLinkOnClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const currentDateTime: Nullable<Moment> = await this.getCurrentDate()
            if (currentDateTime === null) {
                return
            }
            this.setState({
                currentDateTime: currentDateTime,
            })
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        return
    }

    async handleUpdateButtonClick(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        const locationState: LocationState = this.props.location.state
        const {navigate, condominiumCd, facilityId, reserveId} = this.props

        try {
            const returnValue = await NatAsyncCallFunction(this.props.reservationsRepo
                .updateFacilityReservation.bind(
                    this.props.reservationsRepo,
                    this.props.condominiumCd,
                    this.props.facilityId,
                    this.props.reserveId,
                    locationState.reserveInfo,
                ), this.props.navigate)
            if (returnValue === null) {
                return
            }

            setResidentMyReservationsPageSearchDate(locationState.reserveInfo.startDate)
            clearResidentReserveTopPageSearchConditions()

            navigate(
                `/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${reserveId}/updateComplete`,
                {replace: true},
            )
        } catch (e) {
            const httpError: HttpError = e as HttpError
            const {requestError, requestItemErrors} = httpError.responseBody
            const updateErrorResponseBody = new ErrorResponseBody(
                new RequestError(
                    requestError.messageId,
                    Messages.EFACC0017.toString(),
                    requestError.message,
                    requestError.type,
                ),
                requestItemErrors,
            )

            let redirectUrl: string
            if (httpError.responseBody.requestError.messageId === Messages.EOPCC0027.id) {
                redirectUrl = `/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${reserveId}/detail`
            } else {
                redirectUrl = `/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${reserveId}/update`
            }

            navigate(redirectUrl, {
                replace: true,
                state: {
                    previousPageUrl: locationState.previousPageUrl,
                    errorResponseBody: updateErrorResponseBody,
                    reserveInfo: locationState.reserveInfo,
                },
            })
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async handleBackLinkOnClick(): Promise<void> {
        const {navigate, condominiumCd, facilityId, reserveId} = this.props
        const locationState: LocationState = this.props.location.state

        navigate(`/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reservations/${reserveId}/update`, {
            replace: true,
            state: {
                previousPageUrl: locationState.previousPageUrl,
                reserveInfo: locationState.reserveInfo,
            },
        })
    }

    async getCurrentDate(): Promise<Nullable<Moment>> {
        const {currentDateTimeRepo, navigate} = this.props
        let currentDate: Nullable<Moment>
        const currentTimeResponse = await NatAsyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            navigate,
        )
        if (currentTimeResponse === null || currentTimeResponse.details === null) {
            return null
        }
        currentDate = moment(currentTimeResponse.details.currentDateTime)
        return currentDate
    }

    render(): ReactNode {
        const {facilityInfo, reserveInfo} = this.props.location.state
        const {startDate, reservationTitle, sharedUseRoomNumberList, reserveType} = reserveInfo
        const {cancelAvailability, cancelReceptionLimitDate, cancelReceptionLimitTime} = facilityInfo
        const {currentDateTime} = this.state

        if (currentDateTime === null) {
            return null
        }
        if (reserveType !== RESERVATION_TYPE.FIRST_COME.value &&
            reserveType !== RESERVATION_TYPE.LOTTERY.value) {
            return null
        }

        let confirmFormDate: ReserveFacilityConfirmData
        let startDateTimeMoment: Moment

        if (facilityInfo.stayAvailability) {
            const {stayCount} = reserveInfo as NewOvernightRentalFacilityReservation
            confirmFormDate = {
                startDate: startDate,
                startTime: null,
                endDate: null,
                endTime: null,
                stayCount: stayCount,
                reservationTitle: reservationTitle,
                sharedUseRoomNumberList: sharedUseRoomNumberList,
                options: reserveInfo.options,
                paymentInformation: reserveInfo.paymentInformation,
            }
            const checkinTime = facilityInfo.overnightFacilitySetting?.checkinTime ?? null
            startDateTimeMoment = convertToMomentOrError(startDate, checkinTime)
        } else {
            const {startTime, endDate, endTime} = reserveInfo as NewDurationRentalFacilityReservation
            confirmFormDate = {
                startDate: startDate,
                startTime: startTime,
                endDate: endDate,
                endTime: endTime,
                stayCount: null,
                reservationTitle: reservationTitle,
                sharedUseRoomNumberList: sharedUseRoomNumberList,
                options: reserveInfo.options,
                paymentInformation: reserveInfo.paymentInformation,
            }
            startDateTimeMoment = convertToMomentOrError(startDate, startTime)
        }

        let reservationTypeTag: ReactNode
        if (RESERVATION_TYPE.FIRST_COME.value === reserveType) {
            reservationTypeTag = <ReservationTypeTagForFirstCome>
                {RESERVATION_TYPE.FIRST_COME.label}
            </ReservationTypeTagForFirstCome>
        } else {    // RESERVATION_TYPE.LOTTERY.value === reserveType
            reservationTypeTag = <ReservationTypeTagForLottery>
                {RESERVATION_TYPE.LOTTERY.label}
            </ReservationTypeTagForLottery>
        }

        let warningMessage: Optional<ReactNode>
        if (isCancelLimitOver(
            startDateTimeMoment,
            currentDateTime,
            cancelAvailability,
            cancelReceptionLimitDate,
            cancelReceptionLimitTime,
        )) {
            warningMessage =
                <div>
                    {Messages.WFACR0002.toString()}<br/>
                    {Messages.WFACR0003.toString()}
                </div>
        }

        return <>
            <PlainLinkStyledBackButton onClick={this.handleBackLinkOnClick}>戻る</PlainLinkStyledBackButton>
            <BlockSpacer height={'15px'}/>
            <DisplayItemDividerForPageTop/>
            <BlockSpacer height={'30px'}/>
            <StyledPageTitle>予約変更確認</StyledPageTitle>
            <NoticeMessageHeader>
                予約の変更は完了していません。
            </NoticeMessageHeader>
            <NoticeMessageBody>
                予約内容をご確認のうえ、「この内容で変更する」ボタンを押してください。<br/>
                ボタンを押すと予約を変更します。
            </NoticeMessageBody>
            <BlockSpacer height={'20px'}/>
            {reservationTypeTag}
            <BlockSpacer height={'10px'}/>
            <ResidentReserveFacilityConfirmComponent
                facilityInfo={facilityInfo}
                reserveInfo={confirmFormDate}
            />
            {
                warningMessage ?
                    <>
                        <BlockSpacer height={'15px'}/>
                        <WarningMessageArea>
                            <FontAwesomeIcon icon={faExclamationCircle} color={'#F5A623'} size={'lg'}/>
                            <Box width={'5px'} minWidth={'5px'}/>
                            {warningMessage}
                        </WarningMessageArea>
                        <BlockSpacer height={'10px'}/>
                    </>
                    :
                    <BlockSpacer height={'30px'}/>
            }
            <PrimaryStyledButton onClick={this.handleUpdateButtonClick}>この内容で変更する</PrimaryStyledButton>
            <BlockSpacer height={'30px'}/>
            <CenterPlainLinkStyledButton onClick={this.handleBackLinkOnClick}>修正する</CenterPlainLinkStyledButton>
            <BlockSpacer height={'15px'}/>
        </>
    }
}

export default createResidentPage(ResidentReserveFacilityUpdateConfirmPage)
